import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { ReasignarCliente } from "@/components/cliente/ReasignarCliente";

const ORIG_ROLES = ["ejecutivo_credito", "analista_credito", "admin_entidad"];

export function ReasignarLoteUsuario({ targetUser, open, onOpenChange, onDone }) {
  const [clientes, setClientes] = useState([]);
  const [cobradores, setCobradores] = useState([]);
  const [originadores, setOriginadores] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [cobradorId, setCobradorId] = useState("");
  const [origId, setOrigId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const refrescar = () => {
    if (!targetUser) return;
    const uid = targetUser.id;
    api.get("/clientes").then((r) =>
      setClientes((r.data || []).filter((c) => c.originado_por === uid || c.cobrador_id === uid))).catch(() => {});
  };

  useEffect(() => {
    if (!open || !targetUser) return;
    setSel(new Set()); setCobradorId(""); setOrigId(""); setMotivo(""); setSearch("");
    api.get("/usuarios/cobradores").then((r) => setCobradores(r.data || [])).catch(() => {});
    api.get("/usuarios").then((r) =>
      setOriginadores((r.data || []).filter((u) => ORIG_ROLES.includes(u.role) && u.id !== targetUser.id))).catch(() => {});
    refrescar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetUser]);

  const toggle = (cid) => setSel((p) => { const n = new Set(p); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });

  const filtrados = clientes.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return `${c.nombre || c.razon_social || ""} ${c.rfc || ""}`.toLowerCase().includes(s);
  });
  const allSelected = filtrados.length > 0 && filtrados.every((c) => sel.has(c.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(filtrados.map((c) => c.id)));

  const tagOf = (c) => {
    const t = [];
    if (c.originado_por === targetUser?.id) t.push("Originado");
    if (c.cobrador_id === targetUser?.id) t.push("Cartera");
    return t;
  };

  const submit = async () => {
    if (sel.size === 0) { toast.error("Selecciona al menos un cliente."); return; }
    if (!motivo.trim()) { toast.error("Captura el motivo de la reasignación."); return; }
    if (!cobradorId && !origId) { toast.error("Indica el nuevo originador y/o responsable de cartera."); return; }
    setBusy(true);
    try {
      const body = { cliente_ids: [...sel], motivo: motivo.trim() };
      if (origId) body.originado_por = origId;
      if (cobradorId) body.cobrador_id = cobradorId;
      const { data } = await api.post("/clientes/reasignar-lote", body);
      if (data.fallidos?.length) {
        toast.warning(`Reasignados ${data.total}/${data.solicitados}. ${data.fallidos.length} con error: ${data.fallidos.map((f) => f.motivo).join(" · ")}`);
      } else {
        toast.success(`Reasignados ${data.total} cliente(s) y registrados en el historial.`);
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="reasignar-lote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Mover clientes de {targetUser?.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Selecciona clientes y muévelos a otro originador y/o responsable de cartera. Cada cambio queda registrado en el historial de cada cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input data-testid="lote-search" placeholder="Buscar por nombre o RFC..." value={search}
            onChange={(e) => setSearch(e.target.value)} />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">{filtrados.length} cliente(s) de este usuario</span>
            {filtrados.length > 0 && (
              <button type="button" data-testid="lote-toggle-all" className="text-xs text-primary underline" onClick={toggleAll}>
                {allSelected ? "Quitar todos" : "Seleccionar todos"}
              </button>
            )}
          </div>
          <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
            {filtrados.length === 0 && <p className="px-1 py-2 text-xs text-slate-400">Este usuario no tiene clientes asignados.</p>}
            {filtrados.map((c) => (
              <div key={c.id} data-testid={`lote-row-${c.id}`} className="flex items-center gap-2 rounded px-1.5 py-1.5 text-sm hover:bg-slate-50">
                <Checkbox data-testid={`lote-check-${c.id}`} checked={sel.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                <span className="text-slate-700">{c.nombre || c.razon_social}</span>
                <span className="flex gap-1">{tagOf(c).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}</span>
                <span className="ml-auto"><ReasignarCliente cliente={c} onDone={() => { refrescar(); onDone?.(); }} /></span>
              </div>
            ))}
          </div>

          <div>
            <Label className="text-xs">Nuevo originador (opcional)</Label>
            <Select value={origId || "__none"} onValueChange={(v) => setOrigId(v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="lote-originador"><SelectValue placeholder="Sin cambio" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Sin cambio</SelectItem>
                {originadores.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Nuevo responsable de cartera (cobranza, opcional)</Label>
            <Select value={cobradorId || "__none"} onValueChange={(v) => setCobradorId(v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="lote-cobrador"><SelectValue placeholder="Sin cambio" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Sin cambio</SelectItem>
                {cobradores.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Motivo *</Label>
            <Textarea data-testid="lote-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
              placeholder="Justifica la reasignación en lote (queda en el historial de cada cliente)." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
          <Button data-testid="lote-submit" onClick={submit} disabled={busy} className="bg-accent hover:bg-forest-700">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Mover {sel.size > 0 ? `(${sel.size})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
